// Audition (one-shot preview) of a cloud-resident sample without any
// side effects on the local library. Fetches the blob from R2, decodes
// it once, plays through the AudioContext's destination directly, then
// hands back a stop function so the caller can cut the voice early.
//
// Distinct from importCloudBlobToLocal: that path commits the bytes to
// IDB, registers a Strudel handler, and is the gateway to using the
// sample in patterns. Audition is for "let me hear it before I commit"
// — no IDB row, no Strudel registration.
//
// Returns the AudioBuffer alongside the stop fn so callers may keep a
// session-scoped cache and skip the second decode on repeat clicks.
// (Not implemented in v1 — tiny sample sizes + browser HTTP cache make
//  the simple path good enough for now.)

import { cloudSampleUrl } from './playback';
import { playSampleOnce } from '../samples/playback';

export type AuditionResult = {
  buffer: AudioBuffer;
  stop: () => void;
};

export async function auditionCloudSample(
  r2Key: string,
  ctx: AudioContext,
): Promise<AuditionResult> {
  const url = cloudSampleUrl(r2Key);
  if (!url) throw new Error('VITE_R2_PUBLIC_URL not configured');

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(
      `audition fetch failed (${res.status}): ${res.statusText}`,
    );
  }
  const blob = await res.blob();
  const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
  const stop = playSampleOnce(ctx, buffer);
  return { buffer, stop };
}
